import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { conformToMask } from 'react-text-mask';

import {
  newCheckInAction,
  newNextAction,
  NEW_CHECKIN,
  NEW_NEXT,
} from '../../actions/navigation';

import { phoneNumberMask } from '../../utils/masks';
import { states } from '../../utils/states';
import { parseUrlParams, addLusaHeaderParam } from '../../utils/parseUrlParams';
import Container from '../../components/Container';
import ContentWrapper from '../../components/ContentWrapper';
import ApplicationId from '../../components/ApplicationId';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Input from '../../components/Form/Input';
import Validator from '../../components/Validator';
import Button from '../../components/Button';
import Select from '../../components/Form/Select';
import { useConfig } from '../../config/ConfigContext';
import Header from '../../components/Header';
import schema from './schema';
import User from '../../assets/images/pending.svg';
import './style.scss';

const LuwaConfirmation = (props) => {
  const config = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const match = useParams();

  const formRef = useRef(null);

  const { validator: { setValues, values, errors }, checkinData, location, fetch, navigate } = props;
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');
  const isSsn4Mismatch = get(checkinData, 'data.isSsn4Mismatch', get(checkinData, 'state.isSsn4Mismatch'));
  const errorMessage = get(checkinData, 'data.errorMessage', get(checkinData, 'state.errorMessage'));

  const prms = parseUrlParams(window.location.search);

  useEffect(() => {
    if (isEmpty(checkinData)) {
      props.newCheckInAction({
        workflowType,
        key: prms.key,
      });
    } else {
      const initialFormData = {
        applicantFirstName: get(checkinData, 'data.applicantFirstName', get(checkinData, 'state.applicantFirstName')),
        applicantFullName: get(checkinData, 'data.applicantFullName', get(checkinData, 'state.applicantFullName')),
        streetAddress1: get(checkinData, 'data.streetAddress1', get(checkinData, 'state.streetAddress1')),
        city: get(checkinData, 'data.city', get(checkinData, 'state.city')),
        state: get(checkinData, 'data.state', get(checkinData, 'state.state')),
        zipCode: get(checkinData, 'data.zipCode', get(checkinData, 'state.zipCode')),
        phoneNumber: conformToMask(get(checkinData, 'data.phoneNumber', get(checkinData, 'state.phoneNumber')), phoneNumberMask).conformedValue,
      };
      setValues(initialFormData);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (
      get(checkinData, 'stepName', get(checkinData, 'activity')) !== 'KbaIdentityConfirmation'
      && !get(checkinData, 'data.data.returnUrl', get(checkinData, 'state.data.returnUrl'))
      && ((fetch.isDone[`${NEW_CHECKIN}`] && !fetch.isRunning[`${NEW_CHECKIN}`])
      || (fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`]))
    ) {
      const params = parseUrlParams(window.location.search);
      let routeUrl = get(checkinData, 'data.url', '').replace(config.hostUrl, '');
      if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
        routeUrl = addLusaHeaderParam(routeUrl);
      }
      navigate(routeUrl);
    }
  }, [checkinData]);

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    const params = parseUrlParams(window.location.search);
    if (params.key) {
      const { validator: { validate } } = props;
      if (validate(schema).isValid) {
        setIsLoading(true);
        setIsValid(true);
        props.newNextAction({
          data: {
            StreetAddress1: values.streetAddress1,
            City: values.city,
            State: values.state,
            ZipCode: values.zipCode,
            Ssn4: values.Ssn4,
            PhoneNumber: values.phoneNumber,
          },
          workflowType,
          key: params.key,
          success: () => {
            setIsLoading(false);
          },
          fail: (error) => {
            setIsLoading(false);
            props.navigate({
              pathname: addLusaHeaderParam('/luwa/results/denied'),
              search: '',
              state: {
                data: error.data,
              },
            });
          },
        });
      } else {
        validate(schema);
        setIsValid(false);
      }
    }
  };

  return (
    <div className="transition-item wrapper v-center">
      <form onSubmit={handleSubmitForm} ref={formRef}>
        <Header />
        {get(props.checkinData, 'attributes.entityId') ? (
          <ApplicationId id={get(props.checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
        ) : null}
        <ContentWrapper className="content" style={{ paddingTop: 20 }}>
          <Container className="container">
            <FormRow>
              <FormColumn>
                <h2 className="text-left heading-h">Identity Verification</h2>
                <div className="d-flex align-items-center">
                  <img src={User} alt="User Icon" className="user-image" />
                  <div className="pl-10">
                    <div>LendingUSA</div>
                  </div>
                </div>
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                {!isLoading
                  && isSsn4Mismatch
                  && (
                    <div className="pt-10">
                      <p className="form-note text-left">{errorMessage}</p>
                    </div>
                  )}
                <h4 className="pt-20 text-left">ID Check - Personal Information</h4>
                <p className="heading-paragraph pt-10 text-left">Please confirm your home address and phone number. This information, along with your name, will be used to generate a list of questions to verify your identity.</p>
                <h4 className="pt-20 text-left">Required Information (Home Address)</h4>
              </FormColumn>
            </FormRow>
            <FormRow className="pt-10">
              <FormColumn>
                <Input
                  type="text"
                  name="name"
                  onChange={() => {}}
                  label="Applicant Name"
                  value={values.applicantFullName}
                  isDisabled
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="text"
                  name="streetAddress1"
                  label="Street1"
                  value={values.streetAddress1}
                  onChange={handleInputChange}
                  isRequired
                  hasError={!!errors.streetAddress1}
                  errorMessage={errors.streetAddress1}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="text"
                  name="city"
                  value={values.city}
                  label="City"
                  onChange={handleInputChange}
                  isRequired
                  hasError={!!errors.city}
                  errorMessage={errors.city}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Select
                  name="state"
                  value={values.state}
                  onChange={handleInputChange}
                  data={states}
                  label="State"
                  isRequired
                  hasError={!!errors.state}
                  errorMessage={errors.state}
                />
              </FormColumn>
            </FormRow>
            <FormRow sm={12} className="d-flex align-items-center">
              <FormColumn>
                <Input
                  label="Zip"
                  isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                  placeHolder="_____"
                  onChange={handleInputChange}
                  name="zipCode"
                  value={values.zipCode}
                  isRequired
                  hasError={!!errors.zipCode}
                  errorMessage={errors.zipCode}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="tel"
                  name="Ssn4"
                  value={values.Ssn4}
                  onChange={handleInputChange}
                  label="Last 4 digits of SSN"
                  isMasked={[/\d/, /\d/, /\d/, /\d/]}
                  isRequired
                  hasError={!!errors.Ssn4}
                  errorMessage={errors.Ssn4}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="text"
                  name="phoneNumber"
                  isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                  value={conformToMask(values.phoneNumber, phoneNumberMask).conformedValue}
                  onChange={handleInputChange}
                  label="Phone #"
                  isRequired
                  hasError={!!errors.phoneNumber}
                  errorMessage={errors.phoneNumber}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                {!isValid && (
                  <div>
                    <p className="form-note">You must enter required and valid information before you can continue.</p>
                  </div>
                )}
              </FormColumn>
            </FormRow>
          </Container>
        </ContentWrapper>
        <div className="footer">
          <FormRow>
            <FormColumn>
              <Button
                color="primary"
                className="large buttonStyle"
                onClick={handleSubmitForm}
                isLoading={isLoading}
                isDisabled={isLoading}
                title="Submit"
              />
            </FormColumn>
          </FormRow>
        </div>
      </form>
    </div>
  );
};

LuwaConfirmation.propTypes = {
  navigate: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
  fetch: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  newCheckInAction: PropTypes.func.isRequired,
  newNextAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

const mapDispatchToProps = {
  newCheckInAction,
  newNextAction,
};

export default compose(
  Validator(schema),
  connect(mapStateToProps, mapDispatchToProps)
)(LuwaConfirmation);
